/*
  Gift Engine — Pipeline (bendis) uyumlu sürüm.

  - Tek hediye kampanyası: shop.metafields.custom.active_gift_campaign
  - Sepet hedefine göre (collection / product / vendor / all) hediye urununu
    otomatik olarak ekler veya cikarir.
  - Pipeline temasinin "theme:cart:change" / "theme:cart:reload" eventleri
    ile tam uyumludur. Alpine ve mini-cart custom elementine bagimli degildir.
*/

class GiftEngine {
  constructor() {
    this.THEME_CHANGE_EVENT = "theme:cart:change";
    this.THEME_RELOAD_EVENT = "theme:cart:reload";
    this.LEGACY_REFRESH_EVENT = "cart:refresh";
    this.INTERNAL_EVENT = "gift:cart:changed";
    this.ACTION_COOLDOWN_MS = 400;

    this.isUpdating = false;
    this.lastActionAt = 0;
    this.lastRunAt = 0;
    this.RUN_DEBOUNCE_MS = 5;
    this.runTimer = null;
    this.pendingCart = null;

    this.RELOAD_THROTTLE_MS = 600;
    this.lastReloadAt = 0;
    this.didInitialRun = false;

    this.collectionDataCache = null;
    this.collectionDataPromise = null;
  }

  now() {
    return Date.now();
  }

  log(...args) {
    if (window.GIFT_ENGINE_DEBUG) {
      try { console.log("[GiftEngine]", ...args); } catch (e) {}
    }
  }

  cartUrl(suffix) {
    const base = (window.theme && window.theme.routes && window.theme.routes.cart) || "/cart";
    return base + suffix;
  }

  normalizeGidToNumeric(id) {
    if (!id) return "";
    const match = String(id).match(/\/(\d+)$/);
    return match ? match[1] : String(id);
  }

  safeArray(value) {
    return Array.isArray(value) ? value : [];
  }

  async getCart() {
    const response = await fetch(this.cartUrl(".js"), {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    });
    return response.json();
  }

  isGiftItem(item) {
    if (!item || !item.properties) return false;
    return (
      item.properties._gift === "true" ||
      item.properties._gift === true ||
      item.properties._source === "gift_engine"
    );
  }

  findGiftItem(cart, variantId) {
    return (
      this.safeArray(cart.items).find(
        item => this.isGiftItem(item) && String(item.variant_id) === String(variantId)
      ) || null
    );
  }

  getCampaign() {
    return window.GIFT_CAMPAIGN || null;
  }

  getGiftVariantId(campaign) {
    return this.normalizeGidToNumeric(
      campaign?.hediye_urun ||
      campaign?.gift_variant_id ||
      campaign?.gift_variant
    );
  }

  getTiers() {
    const tiers = window.GIFT_CAMPAIGN_TIERS;
    if (!Array.isArray(tiers) || tiers.length === 0) return null;

    return tiers
      .map(t => ({
        min_tutar: Number(t.min_tutar) || 0,
        indirim_yuzdesi: Number(t.indirim_yuzdesi) || 0
      }))
      .sort((a, b) => b.min_tutar - a.min_tutar);
  }

  findActiveTier(tiers, total) {
    return tiers.find(t => total >= t.min_tutar * 100) || null;
  }

  getCartTotal(cart) {
    return this.safeArray(cart.items)
      .filter(item => !this.isGiftItem(item))
      .reduce((sum, item) => sum + item.original_line_price, 0);
  }

  getTargetTotal(cart, campaign, collectionData) {
    const items = this.filterTargetItems(cart, campaign, collectionData);
    return items.reduce((sum, item) => sum + item.original_line_price, 0);
  }

  normalizeMap(map) {
    const output = {};

    Object.keys(map || {}).forEach(productId => {
      output[String(productId)] = this.safeArray(map[productId]).map(id =>
        this.normalizeGidToNumeric(id)
      );
    });

    return output;
  }

  async fetchCollectionData() {
    if (this.collectionDataCache) return this.collectionDataCache;
    if (this.collectionDataPromise) return this.collectionDataPromise;

    this.collectionDataPromise = (async () => {
      try {
        const response = await fetch(
          "/?section_id=gift-collection-data",
          { credentials: "same-origin" }
        );
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const script = doc.querySelector("[data-gift-collection-data]");
        if (!script) return null;

        try {
          const data = JSON.parse(script.textContent);
          this.collectionDataCache = data;
          return data;
        } catch (e) {
          return null;
        }
      } catch (error) {
        return null;
      } finally {
        this.collectionDataPromise = null;
      }
    })();

    return this.collectionDataPromise;
  }

  invalidateCollectionData() {
    this.collectionDataCache = null;
  }

  isInCollection(item, campaign, map) {
    const productCollections = map[String(item.product_id)] || [];
    const targets = this.safeArray(campaign.target_collections).map(id =>
      this.normalizeGidToNumeric(id)
    );

    return productCollections.some(id => targets.includes(String(id)));
  }

  filterTargetItems(cart, campaign, collectionData) {
    const items = this.safeArray(cart.items).filter(item => !this.isGiftItem(item));

    if (campaign.target_scope === "collection") {
      const targets = this.safeArray(campaign.target_collections);
      // Hedef koleksiyon listesi bossa sepetin tamamini hedefle.
      if (targets.length === 0) return items;
      const map = this.normalizeMap(collectionData || {});
      const matched = items.filter(item => this.isInCollection(item, campaign, map));
      // Otomatik "all" koleksiyonu product.collections'da gozukmedigi icin
      // hicbir item eslesmediyse fallback: tum sepet kalemleri.
      if (matched.length === 0) {
        this.log("collection scope matched 0 items, falling back to full cart");
        return items;
      }
      return matched;
    }

    if (campaign.target_scope === "product") {
      const ids = this.safeArray(campaign.target_products).map(product =>
        this.normalizeGidToNumeric(product?.id || product)
      );
      if (ids.length === 0) return items;
      return items.filter(item => ids.includes(String(item.product_id)));
    }

    if (campaign.target_scope === "vendor") {
      const vendors = this.safeArray(campaign.target_vendors).map(String);
      if (vendors.length === 0) return items;
      return items.filter(item => vendors.includes(String(item.vendor)));
    }

    return items;
  }

  checkCondition(cart, campaign, collectionData) {
    const items = this.filterTargetItems(cart, campaign, collectionData);

    if (campaign.condition_type === "quantity") {
      const quantity = items.reduce((sum, item) => sum + item.quantity, 0);
      return quantity >= Number(campaign.condition_value);
    }

    if (campaign.condition_type === "amount") {
      const total = items.reduce((sum, item) => sum + item.original_line_price, 0);
      return total >= Number(campaign.condition_value) * 100;
    }

    return false;
  }

  fireCartChange(cart) {
    if (!cart) return;
    document.dispatchEvent(new CustomEvent(this.THEME_CHANGE_EVENT, {
      detail: { cart },
      bubbles: true
    }));

    const sinceReload = this.now() - this.lastReloadAt;
    if (sinceReload >= this.RELOAD_THROTTLE_MS) {
      this.lastReloadAt = this.now();
      document.dispatchEvent(new CustomEvent(this.THEME_RELOAD_EVENT, {
        bubbles: true
      }));
    } else {
      this.log("reload event throttled", sinceReload);
    }
  }

  async refreshAndFire(cart) {
    try {
      const finalCart = cart || await this.getCart();
      this.fireCartChange(finalCart);
      return finalCart;
    } catch (e) {
      this.log("refreshAndFire failed", e);
      return null;
    }
  }

  async addGift(variantId) {
    this.log("addGift", variantId);

    const response = await fetch(this.cartUrl("/add.js"), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties: {
              _gift: "true",
              _source: "gift_engine"
            }
          }
        ]
      })
    });

    if (!response.ok) {
      let errorBody = null;
      try { errorBody = await response.json(); } catch (e) {}
      console.error(
        "[GiftEngine] /cart/add.js failed",
        response.status,
        "variantId sent:", variantId,
        "shopify response:", errorBody
      );
      return null;
    }

    return this.refreshAndFire();
  }

  async removeGift(lineKey) {
    this.log("removeGift", lineKey);

    const response = await fetch(this.cartUrl("/change.js"), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0
      })
    });

    if (!response.ok) {
      let errorBody = null;
      try { errorBody = await response.json(); } catch (e) {}
      console.error(
        "[GiftEngine] /cart/change.js failed",
        response.status,
        "lineKey sent:", lineKey,
        "shopify response:", errorBody
      );
      return null;
    }

    let updatedCart = null;
    try { updatedCart = await response.json(); } catch (e) {}
    return this.refreshAndFire(updatedCart);
  }

  lockCheckout() {
    const selector = [
      'button[name="checkout"]',
      'input[name="checkout"]',
      'button[name="goto_pp"]',
      'button[name="goto_gc"]',
      '[data-cart-checkout]',
      'a[href="/checkout"]',
      'a[href^="/checkout"]'
    ].join(',');
    document.querySelectorAll(selector).forEach(el => {
      if (el.dataset.giftEngineLocked) return;
      el.dataset.giftEngineLocked = "1";
      if ('disabled' in el) el.disabled = true;
      el.setAttribute('aria-disabled', 'true');
      el.style.pointerEvents = 'none';
      el.style.opacity = '0.6';
    });

    if (this.unlockTimer) clearTimeout(this.unlockTimer);
    this.unlockTimer = setTimeout(() => this.unlockCheckout(), 4000);
  }

  unlockCheckout() {
    if (this.unlockTimer) {
      clearTimeout(this.unlockTimer);
      this.unlockTimer = null;
    }
    document.querySelectorAll('[data-gift-engine-locked]').forEach(el => {
      delete el.dataset.giftEngineLocked;
      if ('disabled' in el) el.disabled = false;
      el.removeAttribute('aria-disabled');
      el.style.pointerEvents = '';
      el.style.opacity = '';
    });
  }

  shouldRemoveGift(cart) {
    const campaign = this.getCampaign();
    if (!campaign || !cart || !Array.isArray(cart.items)) return false;
    const variantId = this.getGiftVariantId(campaign);
    if (!variantId) return false;
    const giftItem = this.findGiftItem(cart, variantId);
    if (!giftItem) return false;

    const tiers = this.getTiers();
    if (tiers) {
      const total = campaign.kademe_kaynagi === "sepet_toplam"
        ? this.getCartTotal(cart)
        : this.getTargetTotal(cart, campaign, this.collectionDataCache);
      const activeTier = this.findActiveTier(tiers, total);
      return !activeTier;
    }

    return !this.checkCondition(cart, campaign, this.collectionDataCache);
  }

  optimisticHideGift() {
    document.querySelectorAll('[data-gift-line]').forEach(el => {
      el.style.transition = 'opacity 120ms';
      el.style.opacity = '0';
      el.style.pointerEvents = 'none';
      el.dataset.giftHidden = '1';
    });
  }

  async run() {
    if (this.now() - this.lastActionAt < this.ACTION_COOLDOWN_MS) return;
    if (this.isUpdating) return;

    const campaign = this.getCampaign();
    if (!campaign) return;

    this.isUpdating = true;
    this.lockCheckout();

    try {
      const variantId = this.getGiftVariantId(campaign);
      if (!variantId) {
        this.log("missing gift variant id");
        return;
      }

      const tiers = this.getTiers();

      const isCollectionScope = campaign.target_scope === "collection";

      const seedCart = this.pendingCart;
      this.pendingCart = null;

      const [cart, collectionData] = await Promise.all([
        seedCart ? Promise.resolve(seedCart) : this.getCart(),
        isCollectionScope ? this.fetchCollectionData() : Promise.resolve(null)
      ]);

      const giftItem = this.findGiftItem(cart, variantId);

      if (tiers) {
        const total = campaign.kademe_kaynagi === "sepet_toplam"
          ? this.getCartTotal(cart)
          : this.getTargetTotal(cart, campaign, collectionData);

        const activeTier = this.findActiveTier(tiers, total);

        if (activeTier && !giftItem) {
          this.lastActionAt = this.now();
          await this.addGift(variantId);
        } else if (!activeTier && giftItem) {
          this.lastActionAt = this.now();
          await this.removeGift(giftItem.key);
        }
      } else {
        const conditionMet = this.checkCondition(cart, campaign, collectionData);

        if (conditionMet && !giftItem) {
          this.lastActionAt = this.now();
          await this.addGift(variantId);
        } else if (!conditionMet && giftItem) {
          this.lastActionAt = this.now();
          await this.removeGift(giftItem.key);
        }
      }
    } catch (error) {
      this.log("run error", error);
    } finally {
      this.isUpdating = false;
      this.unlockCheckout();
    }
  }

  scheduleRun() {
    if (this.getCampaign()) this.lockCheckout();
    if (this.runTimer) return;
    this.runTimer = setTimeout(async () => {
      this.runTimer = null;
      try {
        await this.run();
      } finally {
        if (!this.isUpdating) this.unlockCheckout();
      }
    }, this.RUN_DEBOUNCE_MS);
  }

  isCartWriteUrl(url) {
    if (typeof url !== "string") return false;
    return url.includes("/cart/add") ||
           url.includes("/cart/change") ||
           url.includes("/cart/update") ||
           url.includes("/cart/clear");
  }

  patchFetch() {
    const originalFetch = window.fetch;
    if (!originalFetch || originalFetch.__giftEnginePatched) return;
    const self = this;

    const inFlightSectionAborts = new Set();
    const isApiCartSection = (url) =>
      typeof url === "string" &&
      /[?&]section_id=api-cart-(items|subtotal)\b/.test(url);

    if (!window.__giftEngineAbortSilencer) {
      window.__giftEngineAbortSilencer = true;
      window.addEventListener("unhandledrejection", (event) => {
        const r = event && event.reason;
        if (r && (r.name === "AbortError" || (typeof r.message === "string" && r.message.includes("aborted")))) {
          event.preventDefault();
        }
      });
    }

    const patched = async function (...args) {
      const reqUrl = typeof args[0] === "string" ? args[0] : (args[0] && args[0].url);
      let abortToken = null;

      // Cart write başlar başlamaz checkout'u kilitle — response beklemeden.
      // Böylece kullanıcı silme/quantity tıkladığı an checkout disabled olur.
      if (self.isCartWriteUrl(reqUrl) && self.getCampaign()) {
        self.lockCheckout();
      }

      if (isApiCartSection(reqUrl)) {
        const controller = new AbortController();
        abortToken = controller;
        inFlightSectionAborts.add(controller);

        const init = (args[1] && typeof args[1] === "object") ? Object.assign({}, args[1]) : {};
        const userSignal = init.signal;
        if (userSignal) {
          if (userSignal.aborted) controller.abort();
          else userSignal.addEventListener("abort", () => controller.abort(), { once: true });
        }
        init.signal = controller.signal;
        args = [args[0], init];
      }

      let response;
      try {
        response = await originalFetch.apply(this, args);
      } catch (e) {
        if (abortToken) inFlightSectionAborts.delete(abortToken);
        throw e;
      }
      if (abortToken) inFlightSectionAborts.delete(abortToken);

      try {
        const url = typeof args[0] === "string" ? args[0] : (args[0] && args[0].url);
        if (self.isCartWriteUrl(url)) {
          // Cart yazıldı; bayatlamış section fetch'leri iptal et.
          // Pipeline'ın late response'u drawer'a eski HTML basamasın.
          inFlightSectionAborts.forEach(c => { try { c.abort(); } catch (e) {} });
          inFlightSectionAborts.clear();
        }
        if (self.isCartWriteUrl(url) && !self.isUpdating) {
          if (/\/cart\/(change|update|clear)\.js/.test(url)) {
            try {
              const cloned = response.clone();
              cloned.json().then(data => {
                if (data && Array.isArray(data.items)) {
                  self.pendingCart = data;
                  // Hediye silinecekse DOM'dan optik olarak hemen gizle.
                  if (self.shouldRemoveGift(data)) {
                    self.optimisticHideGift();
                  }
                }
                document.dispatchEvent(new CustomEvent(self.INTERNAL_EVENT));
              }).catch(() => {
                document.dispatchEvent(new CustomEvent(self.INTERNAL_EVENT));
              });
            } catch (e) {
              document.dispatchEvent(new CustomEvent(self.INTERNAL_EVENT));
            }
          } else {
            document.dispatchEvent(new CustomEvent(self.INTERNAL_EVENT));
          }
        }
      } catch (error) {}

      return response;
    };
    patched.__giftEnginePatched = true;
    window.fetch = patched;
  }

  patchXHR() {
    const proto = XMLHttpRequest.prototype;
    if (proto.__giftEngineOpenPatched) return;
    const originalOpen = proto.open;
    const self = this;

    proto.open = function (...args) {
      this.addEventListener("load", function () {
        try {
          if (self.isCartWriteUrl(args[1]) && !self.isUpdating) {
            document.dispatchEvent(new CustomEvent(self.INTERNAL_EVENT));
          }
        } catch (error) {}
      });

      return originalOpen.apply(this, args);
    };
    proto.__giftEngineOpenPatched = true;
  }

  bindEvents() {
    document.addEventListener(this.THEME_CHANGE_EVENT, (e) => {
      if (this.isUpdating) return;
      const cart = e && e.detail && e.detail.cart;
      if (cart && Array.isArray(cart.items)) {
        this.pendingCart = cart;
      }
      this.scheduleRun();
    });

    document.addEventListener(this.THEME_RELOAD_EVENT, () => {
      this.lastReloadAt = this.now();
    }, true);

    document.addEventListener(this.INTERNAL_EVENT, () => {
      if (this.isUpdating) return;
      this.scheduleRun();
    });

    document.addEventListener("theme:cart:init", () => this.scheduleRun());

    const initialRun = () => {
      if (this.didInitialRun) return;
      this.didInitialRun = true;
      setTimeout(() => this.run(), 300);
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initialRun);
    } else {
      initialRun();
    }
  }

  init() {
    this.patchFetch();
    this.patchXHR();
    this.bindEvents();
  }
}

(() => {
  "use strict";
  if (window.__giftEngineLoaded) return;
  window.__giftEngineLoaded = true;
  const giftEngine = new GiftEngine();
  window.GiftEngine = giftEngine;
  giftEngine.init();
})();
